<script setup lang="ts">
import MemberHeader from "@/components/member/MemberHeader.vue";
import DeveloperSidebar from "@/components/developer/DeveloperSidebar.vue";

const retirements = [
  { ref: 'RET-2025-0041', project: 'Solar Park Beta', amount: 200, date: '2025-02-14', purpose: 'Corporate net-zero commitment', beneficiary: 'Acme Corp', status: 'Complete' },
  { ref: 'RET-2025-0008', project: 'Solar Park Beta', amount: 80, date: '2025-01-05', purpose: 'Voluntary offset', beneficiary: 'GreenTech Ltd', status: 'Complete' },
]
</script>

<template>
  <MemberHeader />

  <div class="flex h-screen bg-gray-50">
    <DeveloperSidebar />

    <main class="flex-1 overflow-auto py-8">
      <div class="max-w-4xl mx-auto px-5">

        <nav class="text-sm text-gray-500 mb-4">
          <span class="hover:text-primary cursor-pointer">Dashboard</span>
          <span class="mx-2">/</span>
          <span class="text-gray-800 font-medium">Retire Credits</span>
        </nav>

        <section class="mb-8">
          <div class="bg-white rounded-lg border border-gray-200 p-6">
            <h1 class="text-3xl font-bold text-accent mb-2">Retire Carbon Credits</h1>
            <p class="text-gray-600">Permanently cancel credits to demonstrate greenhouse gas offsetting. Retirement is irreversible and creates a public retirement record.</p>
          </div>
        </section>

        <div class="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
          <p class="text-sm text-red-800">
            <i class="fas fa-exclamation-triangle mr-2"></i>
            <strong>Irreversible Action:</strong> Retiring credits permanently removes them from circulation. This action cannot be undone.
          </p>
        </div>

        <!-- Retirement form -->
        <form class="space-y-6">
          <section class="bg-white rounded-lg border border-gray-200 p-6">
            <h2 class="text-xl font-semibold text-accent mb-6 flex items-center">
              <i class="fas fa-fire text-primary mr-3"></i>Retirement Details
            </h2>
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Select Project / Issuance Batch *</label>
                <select class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary">
                  <option>Select batch</option>
                  <option>CCI-2024-00089-001 — Solar Park Beta (5,840 available)</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Credits to Retire (tCO2e) *</label>
                <input type="number" class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" placeholder="0" />
                <p class="text-xs text-gray-500 mt-1">Available: 5,840 tCO2e</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Purpose of Retirement *</label>
                <select class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary">
                  <option>Select purpose</option>
                  <option>Corporate net-zero commitment</option>
                  <option>Voluntary offset – individual</option>
                  <option>Regulatory compliance</option>
                  <option>NDC contribution</option>
                  <option>Other</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Beneficiary Name</label>
                <input type="text" class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Name of the entity on whose behalf credits are retired" />
              </div>
              <div class="lg:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-2">Additional Notes</label>
                <textarea rows="3" class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Optional notes to appear on the retirement certificate."></textarea>
              </div>
            </div>
          </section>

          <section class="bg-white rounded-lg border border-gray-200 p-6">
            <label class="flex items-start space-x-3 cursor-pointer">
              <input type="checkbox" class="mt-1 rounded border-gray-300 text-primary focus:ring-primary" />
              <span class="text-sm text-gray-700">I understand that this retirement is permanent and irreversible. The selected credits will be cancelled and a public retirement record will be created.</span>
            </label>
          </section>

          <section class="bg-white rounded-lg border border-gray-200 p-6">
            <div class="flex items-center justify-between">
              <button type="button" class="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50">Cancel</button>
              <button type="submit" class="px-8 py-3 bg-red-600 text-white rounded-md font-medium hover:bg-red-700 flex items-center">
                <i class="fas fa-fire mr-2"></i>Confirm Retirement
              </button>
            </div>
          </section>
        </form>

        <!-- Retirement history -->
        <section class="mt-10">
          <h2 class="text-lg font-semibold text-accent mb-4">Retirement History</h2>
          <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table class="w-full text-sm">
              <thead class="bg-gray-50 text-gray-700 text-xs uppercase tracking-wide">
                <tr>
                  <th class="px-5 py-3 text-left">Date</th>
                  <th class="px-5 py-3 text-left">Project</th>
                  <th class="px-5 py-3 text-right">Amount</th>
                  <th class="px-5 py-3 text-left">Purpose</th>
                  <th class="px-5 py-3 text-left">Beneficiary</th>
                  <th class="px-5 py-3 text-left">Reference</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr v-for="r in retirements" :key="r.ref" class="hover:bg-gray-50">
                  <td class="px-5 py-3 text-gray-700">{{ r.date }}</td>
                  <td class="px-5 py-3 text-gray-700">{{ r.project }}</td>
                  <td class="px-5 py-3 text-right font-medium text-red-600">-{{ r.amount }}</td>
                  <td class="px-5 py-3 text-gray-600">{{ r.purpose }}</td>
                  <td class="px-5 py-3 text-gray-600">{{ r.beneficiary }}</td>
                  <td class="px-5 py-3 text-xs text-gray-500">{{ r.ref }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

      </div>
    </main>
  </div>
</template>
